import type { EmployerSnapshot } from '@klukka/client';
import {
  fmt, fmtH, month, orlofStatus, orlofYearLabel, orlofYearOf,
  type AdminLang, type PeriodInfo,
} from '@klukka/core';
import { StackBar } from '@klukka/ui';
import { useState } from 'react';
import {
  analyticsBase,
  calcRange,
  hoursOf,
  monthsOfYear,
  recordedYears,
  totals,
  type AnalyticsScope,
  type EmployeeCalc,
} from '../derive';
import { Fld, KPI, Panel, Select, type Tr } from '../widgets';

/**
 * Analytics cascade: scope (year / period / day) × employee.
 * Everything below the filter bar recomputes for the selection, and the caption
 * always states the range in force so a number is never read out of context.
 */
export function AnalyticsTab({ snap, periodCalcs, pinfo, tr, lang }: {
  snap: EmployerSnapshot;
  periodCalcs: EmployeeCalc[];
  pinfo: PeriodInfo;
  tr: Tr;
  lang: AdminLang;
}) {
  const settings = snap.company.settings;
  const years = recordedYears(snap);
  const [scope, setScope] = useState<AnalyticsScope>('period');
  const [year, setYear] = useState(years[0] ?? Number(snap.serverToday.slice(0, 4)));
  const [day, setDay] = useState(snap.serverToday);
  const [empId, setEmpId] = useState('all');

  const base = analyticsBase(snap, periodCalcs, scope, year, day);
  const rows = empId === 'all' ? base : base.filter((x) => x.emp.id === empId);
  const tot = totals(rows);

  const allHours = rows.reduce((a, x) => a + hoursOf(x.calc), 0);
  const otHours = rows.reduce((a, x) => a + x.calc.hours.yv1H + x.calc.hours.yv2H, 0);
  const sickDays = rows.reduce((a, x) => a + x.calc.rows.filter((r) => r.sick).length, 0);
  const vacDays = rows.reduce((a, x) => a + x.calc.rows.filter((r) => r.vacation).length, 0);

  // Liability is what the company owes right now, so it is always read as of
  // today rather than as of whatever range the filter bar is showing — a
  // vacation debt does not shrink because somebody selected last March.
  const orlofRows = rows.map(({ emp }) => {
    const status = orlofStatus({
      emp,
      settings,
      entries: snap.orlofEntries.filter((o) => o.empId === emp.id),
      asOf: snap.serverToday,
    });
    return {
      emp,
      earned: status.openingDays + status.accruedDays,
      taken: status.takenDays + status.paidDays,
      balance: status.balanceDays,
      // A vacation day costs a day's wages plus payroll tax on top.
      liab: status.balanceDays * status.dayRate * (1 + settings.tryggPct / 100),
    };
  });
  const orlofLiability = orlofRows.reduce((a, x) => a + x.liab, 0);

  const caption =
    scope === 'year'
      ? `01.01.${year} → ${year === Number(snap.serverToday.slice(0, 4)) ? `${snap.serverToday} (YTD)` : `31.12.${year}`}`
      : scope === 'day'
        ? day
        : `${pinfo.start} → ${pinfo.end} · ‹ ›`;

  return (
    <div>
      <Panel style={{ display: 'flex', gap: 18, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="s-fld" style={{ width: 300 }}>
          {tr('scope')}
          <div className="seg3" style={{ marginTop: 4 }}>
            <button className={scope === 'year' ? 'on' : ''} onClick={() => setScope('year')}>{tr('year')}</button>
            <button className={scope === 'period' ? 'on' : ''} onClick={() => setScope('period')}>{tr('period')}</button>
            <button className={scope === 'day' ? 'on' : ''} onClick={() => setScope('day')}>{tr('day')}</button>
          </div>
        </label>

        {scope === 'year' ? (
          <Select
            label={tr('year')}
            width="110px"
            value={year}
            onChange={(v) => setYear(Number(v))}
            options={years.map((y) => ({ value: y, label: String(y) }))}
          />
        ) : null}

        {scope === 'day' ? <Fld label={tr('dayCol')} value={day} onChange={setDay} type="date" width="160px" /> : null}

        <Select
          label={tr('name')}
          width="210px"
          value={empId}
          onChange={setEmpId}
          options={[
            { value: 'all', label: tr('allEmployees') },
            ...snap.employees.filter((e) => !e.inactive).map((e) => ({ value: e.id, label: e.name })),
          ]}
        />

        <span className="dimtx" style={{ fontSize: 12, paddingBottom: 9 }}>{caption}</span>
      </Panel>

      <div className="kpis">
        <KPI
          label={tr('totalPayroll')}
          value={fmt(tot.cost)}
          sub={`${tr('gross').toLowerCase()} ${fmt(tot.gross)} · ${tr('net').toLowerCase()} ${fmt(tot.net)}`}
          accent
        />
        <KPI
          label={tr('totalHours')}
          value={`${fmtH(allHours)} klst`}
          sub={`DV ${fmtH(allHours - otHours)} · YV ${fmtH(otHours)}`}
        />
        <KPI
          label={tr('overtime')}
          value={`${fmtH(otHours)} klst · ${allHours ? Math.round((otHours / allHours) * 100) : 0}%`}
          sub={tr('shareOfHours')}
        />
        <KPI
          label={tr('absenceShort')}
          value={`${sickDays + vacDays} d.`}
          sub={`${tr('sick').toLowerCase()} ${sickDays} · ${tr('vacationLbl').toLowerCase()} ${vacDays}`}
        />
      </div>

      <Panel title={`${tr('costMix')} · ${fmt(tot.cost)}`}>
        <StackBar
          total={tot.cost || 1}
          parts={[
            { label: tr('gross'), value: tot.gross, color: 'var(--ink2)' },
            { label: tr('orlofLbl'), value: tot.orlof, color: 'var(--primary)' },
            { label: tr('pensionTot'), value: tot.pension, color: 'var(--live)' },
            { label: tr('tryggLbl'), value: tot.trygg, color: 'var(--ot2)' },
            { label: tr('fundsLbl'), value: tot.funds, color: 'var(--ink3)' },
          ]}
        />
      </Panel>

      {/* Named for the orlofsár it belongs to (2026/27), not the calendar year. */}
      <Panel title={`${tr('orlofLiab')} ${orlofYearLabel(orlofYearOf(snap.serverToday))}`}>
        <table className="tbl">
          <thead>
            <tr>
              <th>{tr('name')}</th><th>{tr('earned')}</th><th>{tr('taken')}</th>
              <th>{tr('balance')}</th><th>{tr('liabIsk')}</th>
            </tr>
          </thead>
          <tbody>
            {orlofRows.map(({ emp, earned, taken, balance, liab }) => (
              <tr key={emp.id}>
                <td><b>{emp.name}</b></td>
                <td className="mono">{fmtH(earned)} d.</td>
                <td className="mono">{fmtH(taken)} d.</td>
                <td className="mono"><b>{fmtH(balance)} d.</b></td>
                <td className="mono">{fmt(liab)}</td>
              </tr>
            ))}
            <tr className="sum">
              <td colSpan={4}>{tr('total')}</td>
              <td className="mono"><b>{fmt(orlofLiability)}</b></td>
            </tr>
          </tbody>
        </table>
      </Panel>

      <Panel title={tr('hoursMix')}>
        {rows.map(({ emp, calc }) => {
          const total = hoursOf(calc) || 1;
          return (
            <div key={emp.id} className="hmix">
              <span className="hmix-name">{emp.name.split(' ')[0]}</span>
              <div className="stack thin">
                <div style={{ width: `${(calc.hours.dvH / total) * 100}%`, background: 'var(--ink2)' }} />
                <div style={{ width: `${(calc.hours.yv1H / total) * 100}%`, background: 'var(--live)' }} />
                <div style={{ width: `${(calc.hours.yv2H / total) * 100}%`, background: 'var(--ot2)' }} />
              </div>
              <span className="mono dimtx" style={{ fontSize: 12 }}>{fmtH(hoursOf(calc))} klst</span>
            </div>
          );
        })}
        <div className="legend3" style={{ marginTop: 8 }}>
          <span><i style={{ background: 'var(--ink2)' }} />DV</span>
          <span><i style={{ background: 'var(--live)' }} />YV1</span>
          <span><i style={{ background: 'var(--ot2)' }} />YV2</span>
        </div>
      </Panel>

      <MonthlyMatrix snap={snap} year={year} empId={empId} tr={tr} lang={lang} />
    </div>
  );
}

/** Months as columns, people as rows — the shape a payroll clerk reads fastest. */
function MonthlyMatrix({ snap, year, empId, tr, lang }: {
  snap: EmployerSnapshot;
  year: number;
  empId: string;
  tr: Tr;
  lang: AdminLang;
}) {
  const settings = snap.company.settings;
  const months = monthsOfYear(year, snap.serverToday);
  const people = snap.employees.filter((e) => !e.inactive && (empId === 'all' || e.id === empId));

  const matrix = people.map((emp) => ({
    emp,
    cells: months.map(({ from, to }) => {
      const c = calcRange(emp, settings, from, to);
      return { gross: c.gross, hours: hoursOf(c) };
    }),
  }));
  const colTotals = months.map((_, i) =>
    matrix.reduce((a, r) => ({ gross: a.gross + r.cells[i].gross, hours: a.hours + r.cells[i].hours }), { gross: 0, hours: 0 }),
  );
  const sum = (cells: Array<{ gross: number; hours: number }>) =>
    cells.reduce((a, c) => ({ gross: a.gross + c.gross, hours: a.hours + c.hours }), { gross: 0, hours: 0 });

  const cell = (c: { gross: number; hours: number }, bold?: boolean) =>
    c.gross > 0 ? (
      <div>
        <span className="mono" style={bold ? { fontWeight: 700 } : undefined}>{fmt(c.gross)}</span>
        <br />
        <span className="dimtx mono" style={{ fontSize: 11 }}>{fmtH(c.hours)} klst</span>
      </div>
    ) : (
      <span className="dimtx">—</span>
    );

  return (
    <Panel title={`${tr('monthlyOverview')} ${year}`}>
      <table className="tbl">
        <thead>
          <tr>
            <th>{tr('name')}</th>
            {months.map(({ month: m }) => <th key={m}>{month(lang, m)}</th>)}
            <th>{tr('total')}</th>
          </tr>
        </thead>
        <tbody>
          {matrix.map(({ emp, cells }) => (
            <tr key={emp.id}>
              <td><b>{emp.name.split(' ')[0]}</b></td>
              {cells.map((c, i) => <td key={i}>{cell(c)}</td>)}
              <td>{cell(sum(cells), true)}</td>
            </tr>
          ))}
          <tr className="sum">
            <td>{tr('total')}</td>
            {colTotals.map((c, i) => <td key={i}>{cell(c, true)}</td>)}
            <td>{cell(sum(colTotals), true)}</td>
          </tr>
        </tbody>
      </table>
    </Panel>
  );
}
